import { TOP_NAV_TEAM_SWITCHER } from '@/features/group-security/constants';
import { useGroupSecurityCohort } from '@/hooks/useGroupSecurityCohort';

/**
 * Does the TOP BAR own the teamspace chooser? (#2452)
 *
 * Exactly two components read this and they must never disagree:
 *   - `TopNavigation` renders `TeamScopeButton` in the bell's slot when it is TRUE;
 *   - `UserMenu` renders the in-menu `TeamSwitcher` when it is FALSE.
 *
 * One predicate in one place, because the first version of this gate did NOT do that. The cohort
 * check was added to `TopNavigation` alone while `UserMenu` kept the older
 * `!TOP_NAV_TEAM_SWITCHER` test — which is a constant `true`, so for a legacy account the top bar
 * correctly withheld the Group Security chooser and the menu never brought the old switcher back.
 * The account ended up with NO team switcher anywhere. That is precisely the failure
 * `.claude/rules/frontend/white-label-branding.md` describes: gate one render path and not its
 * twin. A shared predicate makes the two physically unable to drift.
 *
 * TRUE  — Group Security cohort AND the flag: the chooser lives in the top bar (N-Q3).
 * FALSE — either a legacy account (the chooser does not exist on `origin/main`, and its "All
 *         Teamspaces" scope is meaningless without cross-teamspace reach) or the flag is off. The
 *         nav then reverts to main's shape: notification bell in the top bar, `TeamSwitcher` in
 *         the user menu.
 */
export function useTopNavOwnsTeamScope(): boolean {
  // The hook is called UNCONDITIONALLY and the constant applied after. `CONSTANT && useHook()`
  // short-circuits, which is a Rules-of-Hooks violation the moment the flag is flipped off — the
  // hook count would change between renders. Caught by eslint, kept explicit here.
  const cohort = useGroupSecurityCohort();
  return TOP_NAV_TEAM_SWITCHER && cohort;
}
